import { useRef, useState } from "react";
import { useStoreContext } from "../../contexts/store.context";
import type { AttributeFilterOptionType } from "../../types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBroom, faCancel, faCheck, faCross, faSearch, faX, faXmark } from "@fortawesome/free-solid-svg-icons";
import { twMerge } from "tailwind-merge";
import { SearchInput } from "~/components/controls";


interface AttributeFilterProps {
    name: string;
    options: AttributeFilterOptionType[];
}


const AttributeFilterOption = ({ option }: { option: AttributeFilterOptionType }) => {
    const { setAttributeFilter, isFetching, attributeFilters } = useStoreContext()
    const isChecked = attributeFilters.includes(option.id);

    const handleChange = (e) => {
        if (e.target.checked) {
            setAttributeFilter((prev) => [...prev, option.id])
        } else {
            setAttributeFilter((prev) => prev.filter((id) => id !== option.id))
        }
    }

    return <label className="flex flex-row items-center gap-2 cursor-pointer hover:underline underline-offset-4">
        <input
            hidden
            type="checkbox"
            checked={isChecked}
            onChange={handleChange}
            disabled={isFetching}
        />
        <div className={twMerge(
            "flex items-center justify-center rounded-sm border h-4 w-4 leading-4",
            isChecked ? "bg-violet-600 border-violet-600" : "border-white"
        )}>
            {isChecked && <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />}
        </div>
        <span>{option.value}</span>
    </label>
}

export const AttributeFilter = ({ name, options }: AttributeFilterProps) => {
    const { attributeFilters, setAttributeFilter } = useStoreContext()
    const [search, setSearch] = useState("");
    const filteredOptions = options.filter(option => option.value.toLowerCase().includes(search.toLowerCase()));
    const hasFilter = options.some(option => attributeFilters.includes(option.id));

    const clearFilter = () => {
        setSearch("");
        setAttributeFilter((prev) => prev.filter((id) => !options.map(o => o.id).includes(id)));
    }


    return <details className="select-none border border-transparent rounded-sm [background:linear-gradient(black,black)_content-box,linear-gradient(45deg,#C91FFF75,#03CEFF75)_border-box]">
        <summary className="relative p-4 cursor-pointer underline-offset-4 hover:underline hover:bg-white/5">
            <span>{name}</span>
            {hasFilter && <button onClick={clearFilter} className="absolute right-4 text-white hover:text-yellow-500">
                <span className="text-2xs uppercase pr-2">Clear</span>
                <FontAwesomeIcon icon={faBroom} title="clear" />
            </button>}
        </summary>
        <div className="flex flex-col gap-4 p-4 border-t border-white/20">
            <SearchInput value={search} onChange={setSearch} />
            {filteredOptions.map(option => <AttributeFilterOption key={option.id} option={option} />)}
        </div>
    </details >
}

export const AttributeFilterGroup = () => {
    const { availableFiltering, attributeFilters, setAttributeFilter } = useStoreContext()
    const availableAttributeFilters = Object.entries(availableFiltering?.attributes ?? [])
    const hasAttributeFilters = attributeFilters.length > 0;

    const clearAllAttributeFilters = () => {
        setAttributeFilter([]);
    }

    if (availableAttributeFilters.length === 0) return null;
    return <div className="flex flex-col gap-8">
        <header className="flex flex-row justify-between uppercase font-black">
            <span>Attributes</span>
            {hasAttributeFilters && <button onClick={clearAllAttributeFilters} className="text-white hover:text-yellow-500">
                <span className="text-2xs uppercase pr-2">Clear all</span>
                <FontAwesomeIcon icon={faBroom} title="clear" />
            </button>}
        </header>
        <section className="flex flex-col gap-2">
            {availableAttributeFilters?.map(([name, options]) => <AttributeFilter key={name} name={name} options={options} />)}
        </section>
    </div>
}